import { render } from "../ui/app.js";

import { C, DOC_INFO } from "../core/config.js";
import { state } from "../core/state.js";
import { btnStyle, escapeHTML, fmtCOP } from "../core/utils.js";
import { getAlertas } from "./alertas.js";
import { crearDesgloseCard, getEstadosDesglose } from "./asistenteIA.js";
import { getTotalCxP, getTotalPagado } from "./documentos.js";
import { bannerPuestaEnMarcha } from "./puestaEnMarcha.js";
import { calcularMargenPeriodoOps, getAlertasVigencia, getPeriodoRango } from "./reportes.js";

// ── Sistema visual del panel: compacto y corporativo ────────────────────────
// Tarjetas sobrias (borde fino, sombra mínima, radio moderado), densidad alta
// y acentos discretos. Nada de números gigantes ni badges grandes.
const CARD = "background:#fff;border:1px solid #e6ecf3;border-radius:10px;box-shadow:0 1px 2px rgba(16,35,74,.04);";
const SEC = (t) => `<div style="font-size:10.5px;font-weight:700;letter-spacing:.7px;color:${C.dim};text-transform:uppercase;margin:22px 3px 11px;">${t}</div>`;

export function renderDashboard() {
  const totalCT = state.data.cotizaciones.reduce((s,d) => s + (Number(d.total)||0), 0);
  const totalOP = state.data.pedidos.reduce((s,d) => s + (Number(d.total)||0), 0);

  // Estadísticas principales
  const ctPendientes = state.data.cotizaciones.filter(d => d.estado !== "convertida" && d.estado !== "rechazada").length;
  const opPendientes = state.data.pedidos.filter(d => d.estado !== "convertida" && d.estado !== "cancelada").length;
  const ocpPendientes = state.data.ocp.filter(d => d.estado !== "recibida" && d.estado !== "cancelada").length;
  const rcPorFacturar = state.data.remisiones.filter(d => d.estado !== "facturada" && d.estado !== "cancelada").length;
  const totalFacturado = (state.data.facturas||[]).reduce((s,f)=>s+(Number(f.total)||0),0);
  const factPendCobro = (state.data.facturas||[]).filter(f=>f.estado==="emitida"||f.estado==="vencida").length;
  const factPagadas = (state.data.facturas||[]).filter(f=>f.estado==="pagada").length;
  const totalCobrado = (state.data.facturas||[]).filter(f=>f.estado==="pagada").reduce((s,f)=>s+(Number(f.total)||0),0);
  const porCobrar = totalFacturado - totalCobrado;
  const porPagar = getTotalCxP();

  // ── HERO: saludo + fecha + logo (compacto) ─────────────────────────────────
  let saludo = "Hola", fechaHoy = "";
  try {
    const h = new Date().getHours();
    saludo = h < 12 ? "Buenos días" : h < 19 ? "Buenas tardes" : "Buenas noches";
    fechaHoy = new Date().toLocaleDateString("es-CO", { weekday:"long", year:"numeric", month:"long", day:"numeric" });
  } catch {}
  const primerNombre = (state.usuarioNombre || "").trim().split(/\s+/)[0] || "";

  const heroChip = (label, valor, color) => `
    <div style="background:#fff;border:1px solid #e6ecf3;border-radius:9px;padding:7px 13px;min-width:120px;">
      <div style="font-size:9px;font-weight:700;letter-spacing:.4px;color:${C.muted};text-transform:uppercase;">${label}</div>
      <div style="font-size:14px;font-weight:800;color:${color};margin-top:2px;">${valor}</div>
    </div>`;

  const hero = `
    <div style="position:relative;overflow:hidden;${CARD}border-radius:12px;margin-bottom:16px;background:linear-gradient(135deg,#ffffff 0%,#f6f9fd 60%,#f1f5fb 100%);">
      <div style="height:3px;width:100%;background:linear-gradient(90deg,${C.mathiz},${C.gold});"></div>
      <div style="display:flex;align-items:center;justify-content:space-between;gap:18px;flex-wrap:wrap;padding:15px 20px;">
        <div style="display:flex;align-items:center;gap:16px;min-width:230px;">
          <div style="background:#fff;border:1px solid #e6ecf3;border-radius:10px;padding:8px 13px;">
            <img src="${window.LOGO}" alt="" style="height:38px;max-width:160px;object-fit:contain;display:block;image-rendering:-webkit-optimize-contrast;"/>
          </div>
          <div>
            <div style="font-size:16px;font-weight:800;color:${C.mathiz};letter-spacing:-.2px;">${saludo}${primerNombre ? `, ${escapeHTML(primerNombre)}` : ""}</div>
            <div style="font-size:11.5px;color:${C.muted};margin-top:2px;text-transform:capitalize;">${escapeHTML(fechaHoy)}</div>
          </div>
        </div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;justify-content:flex-end;">
          ${heroChip("Por cobrar", fmtCOP(porCobrar), "#059669")}
          ${heroChip("Por pagar", fmtCOP(porPagar), "#d97706")}
        </div>
      </div>
    </div>`;

  // ── BANNER RECORDATORIO DE BACKUP ──────────────────────────────────────────
  let backupBannerHTML = "";
  try {
    const lastBackup = localStorage.getItem("erp:last_backup");
    const diasSinBackup = lastBackup
      ? Math.floor((Date.now() - new Date(lastBackup).getTime()) / (1000*60*60*24))
      : 999;
    if (diasSinBackup >= 7) {
      const msg = lastBackup
        ? `Último respaldo hace ${diasSinBackup} días (${new Date(lastBackup).toLocaleDateString("es-CO")})`
        : "Nunca has exportado un respaldo de tus datos";
      backupBannerHTML = `
        <div data-action="ir-respaldos" class="dcard clk" style="${CARD}cursor:pointer;padding:11px 15px;margin-bottom:9px;display:flex;justify-content:space-between;align-items:center;gap:12px;border-left:3px solid #e0a53a;">
          <div style="display:flex;gap:11px;align-items:center;">
            <span style="font-size:15px;opacity:.85;">💾</span>
            <div>
              <div style="font-weight:700;color:${C.text};font-size:12.5px;">Exporta tu respaldo de datos</div>
              <div style="font-size:11px;color:${C.muted};margin-top:1px;">${msg}</div>
            </div>
          </div>
          <button data-action="export-json" style="${btnStyle("#d97706",null,true)};white-space:nowrap;">⬇️ Descargar</button>
        </div>`;
    }
  } catch {}

  // ── ALERTAS DE VIGENCIA + CENTRO DE ALERTAS ────────────────────────────────
  const alertas = getAlertasVigencia();
  const tieneAlertas = alertas.porVencer.length > 0 || alertas.vencida.length > 0;

  let alertasHTML = "";
  if (tieneAlertas) {
    let filas = "";
    if (alertas.vencida.length > 0) {
      const nums = alertas.vencida.slice(0,3).map(ct => escapeHTML(ct.numero)).join(", ");
      const mas = alertas.vencida.length > 3 ? ` y ${alertas.vencida.length - 3} más` : "";
      filas += `
        <div style="display:flex;align-items:center;gap:10px;background:#fdf5f5;border-radius:8px;padding:9px 12px;">
          <span style="width:7px;height:7px;border-radius:50%;background:${C.red};flex:0 0 auto;"></span>
          <div style="flex:1;">
            <span style="font-weight:700;color:#8f3d3d;font-size:12px;">${alertas.vencida.length} cotizaciones vencidas</span>
            <span style="font-size:10.5px;color:${C.muted};margin-left:6px;">${nums}${mas}</span>
          </div>
        </div>`;
    }
    if (alertas.porVencer.length > 0) {
      const dets = alertas.porVencer.slice(0,3).map(ct => `${escapeHTML(ct.numero)} (${ct.diasRestantes} d)`).join(", ");
      const mas = alertas.porVencer.length > 3 ? ` y ${alertas.porVencer.length - 3} más` : "";
      filas += `
        <div style="display:flex;align-items:center;gap:10px;background:#fdfaf3;border-radius:8px;padding:9px 12px;">
          <span style="width:7px;height:7px;border-radius:50%;background:#d9a520;flex:0 0 auto;"></span>
          <div style="flex:1;">
            <span style="font-weight:700;color:#7a6320;font-size:12px;">${alertas.porVencer.length} cotizaciones por vencer</span>
            <span style="font-size:10.5px;color:${C.muted};margin-left:6px;">${dets}${mas}</span>
          </div>
        </div>`;
    }
    alertasHTML += `
      <div class="dcard" style="${CARD}padding:12px 14px;margin-bottom:9px;border-left:3px solid ${C.red};">
        <div style="font-size:10px;font-weight:700;letter-spacing:.5px;color:${C.dim};text-transform:uppercase;margin-bottom:9px;">Alertas de cotizaciones</div>
        <div style="display:flex;flex-direction:column;gap:7px;">${filas}</div>
      </div>`;
  }

  const alGen = getAlertas();
  const alGenTotal = alGen.opsEntrega.length + alGen.ctSeguimiento.length + alGen.cxpDescuento.length;
  if (alGenTotal > 0) {
    alertasHTML += `
      <div data-action="ir-alertas" class="dcard clk" style="${CARD}cursor:pointer;padding:11px 15px;margin-bottom:9px;display:flex;justify-content:space-between;align-items:center;gap:12px;border-left:3px solid #e0a53a;">
        <div style="display:flex;gap:11px;align-items:center;">
          <span style="width:8px;height:8px;border-radius:50%;background:#d9a520;flex:0 0 auto;"></span>
          <div>
            <div style="font-weight:700;color:${C.text};font-size:12.5px;">Centro de Alertas · ${alGenTotal} pendiente${alGenTotal!==1?"s":""}</div>
            <div style="font-size:11px;color:${C.muted};margin-top:1px;">${alGen.opsEntrega.length} entrega${alGen.opsEntrega.length!==1?"s":""} de OP · ${alGen.ctSeguimiento.length} CT por seguir · ${alGen.cxpDescuento.length} descuento${alGen.cxpDescuento.length!==1?"s":""} CxP por vencer</div>
          </div>
        </div>
        <button data-action="ir-alertas" style="${btnStyle("#d9a520",null,true)}">Ver alertas</button>
      </div>`;
  }

  // ── FLUJO DE TRABAJO (compacto, alineado a la izquierda) ────────────────────
  const stage = (color, icon, label, value, sub, nav) => `
    <div ${nav ? `data-nav="${nav}"` : ""} class="dcard${nav ? " clk" : ""}" style="${CARD}flex:1;min-width:120px;padding:12px 14px;border-left:3px solid ${color};">
      <div style="display:flex;align-items:center;gap:7px;">
        <span style="font-size:13px;opacity:.8;">${icon}</span>
        <span style="font-size:9.5px;font-weight:700;color:${color};text-transform:uppercase;letter-spacing:.4px;">${label}</span>
      </div>
      <div style="font-size:22px;font-weight:800;color:${C.text};margin-top:7px;line-height:1;">${value}</div>
      <div style="font-size:10px;color:${C.muted};margin-top:3px;">${sub}</div>
    </div>`;

  const chev = `<div class="chev">›</div>`;
  const flowDiagram = `
    <div class="flow" style="display:flex;align-items:stretch;gap:8px;">
      ${stage(C.blue, "📋", "Cotizaciones", ctPendientes, "por convertir", "CT")}
      ${chev}
      ${stage(C.amber, "⚙️", "En producción", opPendientes, "en proceso", "OP")}
      ${chev}
      <div style="display:flex;flex-direction:column;gap:8px;flex:1;min-width:120px;">
        <div data-nav="OCP" class="dcard clk" style="${CARD}flex:1;padding:9px 12px;border-left:3px solid ${C.purple};display:flex;align-items:center;justify-content:space-between;gap:8px;">
          <div><div style="font-size:9px;font-weight:700;color:${C.purple};text-transform:uppercase;letter-spacing:.3px;">🛒 Compras</div></div>
          <div style="font-size:18px;font-weight:800;color:${C.text};">${ocpPendientes}</div>
        </div>
        <div data-nav="RC" class="dcard clk" style="${CARD}flex:1;padding:9px 12px;border-left:3px solid ${C.cyan};display:flex;align-items:center;justify-content:space-between;gap:8px;">
          <div><div style="font-size:9px;font-weight:700;color:${C.cyan};text-transform:uppercase;letter-spacing:.3px;">📮 Remisiones</div></div>
          <div style="font-size:18px;font-weight:800;color:${C.text};">${rcPorFacturar}</div>
        </div>
      </div>
      ${chev}
      ${stage("#059669", "💰", "Facturas", (state.data.facturas||[]).length, `${factPendCobro} por cobrar`, "FACT")}
    </div>`;

  // ── RESUMEN POR DOCUMENTO (KPIs) ───────────────────────────────────────────
  const kpis = ["CT","OP","OCP","RC","FACT"].map(tipo => {
    const info = DOC_INFO[tipo];
    const count = (state.data[info.key]||[]).length;
    return `
      <div data-nav="${tipo}" class="dcard clk" style="${CARD}padding:13px 15px;position:relative;overflow:hidden;">
        <div style="position:absolute;left:0;top:0;bottom:0;width:3px;background:${info.color};"></div>
        <div style="font-size:9.5px;color:${C.muted};font-weight:700;letter-spacing:.4px;text-transform:uppercase;">${escapeHTML(info.plural)}</div>
        <div style="font-size:23px;font-weight:800;color:${info.color};margin-top:5px;line-height:1;">${count}</div>
        <div style="font-size:9.5px;color:${C.muted};margin-top:4px;">${count === 1 ? "documento" : "documentos"}</div>
      </div>`;
  }).join("");

  // Desglose por estado
  const ctEstados = getEstadosDesglose(state.data.cotizaciones, "CT");
  const opEstados = getEstadosDesglose(state.data.pedidos, "OP");
  const ocpEstados = getEstadosDesglose(state.data.ocp, "OCP");
  const rcEstados = getEstadosDesglose(state.data.remisiones, "RC");

  // ── MARGEN DE UTILIDAD ─────────────────────────────────────────────────────
  const margenSection = (function(){
    const mp = state.dashMargenPeriodo || "mes";
    const periodos = [
      { id:"dia", label:"Hoy" }, { id:"semana", label:"Semana" }, { id:"mes", label:"Mes" },
      { id:"trimestre", label:"Trimestre" }, { id:"semestre", label:"Semestre" }, { id:"anio", label:"Año" }, { id:"todo", label:"Todo" }
    ];
    const rango = getPeriodoRango(mp);
    const m = calcularMargenPeriodoOps(rango.desde, rango.hasta);
    const margen = m.margen;
    const margenColor  = margen === null ? C.muted : margen >= 25 ? "#16a34a" : margen >= 15 ? "#d97706" : "#dc2626";
    const margenIcon   = margen === null ? "➖" : margen >= 25 ? "📈" : margen >= 15 ? "⚠️" : "📉";
    const etiqueta = periodos.find(p=>p.id===mp)?.label || mp;
    const btnsPeriodo = periodos.map(p => `
      <button data-action="dash-margen-periodo" data-periodo="${p.id}" style="
        padding:5px 12px;font-size:10px;font-weight:700;border-radius:16px;border:none;cursor:pointer;font-family:inherit;
        background:${mp===p.id?C.accent:"#eef2f7"};color:${mp===p.id?"white":C.muted};transition:all .15s;
      ">${p.label}</button>`).join("");
    const tile = (label, valor, color, sub) => `
      <div style="background:#fbfcfe;border:1px solid #eef2f7;border-radius:9px;padding:10px 12px;">
        <div style="font-size:9px;color:${C.muted};font-weight:700;letter-spacing:.3px;text-transform:uppercase;">${label}</div>
        <div style="font-size:14.5px;font-weight:800;color:${color};margin-top:4px;">${valor}</div>
        <div style="font-size:9.5px;color:${C.muted};margin-top:2px;">${sub}</div>
      </div>`;
    return `
      <div class="dcard" style="${CARD}padding:17px 19px;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:14px;">
          <div>
            <div style="font-size:10px;font-weight:700;color:${C.dim};letter-spacing:.6px;text-transform:uppercase;margin-bottom:6px;">Margen de utilidad general · ${etiqueta}</div>
            <div style="display:flex;align-items:baseline;gap:9px;">
              <div style="font-size:34px;font-weight:900;color:${margenColor};line-height:1;">${margen === null ? "N/D" : margen.toFixed(1) + "%"}</div>
              <div style="font-size:19px;">${margenIcon}</div>
            </div>
            <div style="font-size:10px;color:${C.muted};margin-top:5px;">Fórmula: 1 − (costos + gastos) ÷ ventas</div>
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:5px;justify-content:flex-end;max-width:330px;">${btnsPeriodo}</div>
        </div>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(145px,1fr));gap:9px;margin-top:16px;">
          ${tile("Ventas totales", fmtCOP(m.ventaTotal), "#0284c7", `${m.nFacturas} factura${m.nFacturas!==1?"s":""}`)}
          ${tile("Costos (OPs)", fmtCOP(m.costoTotal), "#dc2626", `${m.nOps} OP${m.nOps!==1?"s":""} en período`)}
          ${tile("Gastos contables (Cl. 5)", fmtCOP(m.gastosContab), "#4C5FA8", "vía PUC / asientos")}
          ${tile("Total costos + gastos", fmtCOP(m.totalCostosGastos), "#d97706", margen!==null?`${((m.totalCostosGastos/m.ventaTotal)*100).toFixed(1)}% de ventas`:"—")}
        </div>
        ${margen !== null && margen < 15 ? `<div style="margin-top:12px;padding:8px 11px;background:#fdf5f5;border-radius:8px;font-size:11px;color:#991b1b;font-weight:600;">Margen crítico — revisa tus costos de producción y gastos operacionales</div>` : ""}
        ${margen !== null && margen >= 15 && margen < 25 ? `<div style="margin-top:12px;padding:8px 11px;background:#fdfaf3;border-radius:8px;font-size:11px;color:#92400e;font-weight:600;">Margen moderado — hay oportunidad de mejorar la rentabilidad</div>` : ""}
        ${margen === null ? `<div style="margin-top:12px;padding:8px 11px;background:#f8fafc;border-radius:8px;font-size:11px;color:${C.muted};">Sin facturas registradas en el período seleccionado</div>` : ""}
      </div>`;
  })();

  // ── INDICADORES FINANCIEROS ────────────────────────────────────────────────
  const indicador = (color, icon, label, valor, sub, nav) => `
    <div ${nav ? `data-nav="${nav}"` : ""} class="dcard${nav ? " clk" : ""}" style="${CARD}padding:13px 15px;position:relative;overflow:hidden;">
      <div style="position:absolute;left:0;top:0;bottom:0;width:3px;background:${color};"></div>
      <div style="font-size:9.5px;color:${C.muted};font-weight:700;letter-spacing:.3px;text-transform:uppercase;">${icon} ${label}</div>
      <div style="font-size:18px;font-weight:800;color:${color};margin-top:6px;">${valor}</div>
      <div style="font-size:10px;color:${C.muted};margin-top:3px;">${sub}</div>
    </div>`;

  const dobleCard = (nav, izq, der, borde) => `
    <div data-nav="${nav}" class="dcard clk" style="${CARD}padding:14px 16px;display:flex;justify-content:space-between;align-items:center;gap:18px;border-left:3px solid ${borde};">
      <div>
        <div style="font-size:9.5px;color:${C.muted};font-weight:700;letter-spacing:.3px;text-transform:uppercase;margin-bottom:4px;">${izq.label}</div>
        <div style="font-size:19px;font-weight:800;color:${izq.color};">${izq.valor}</div>
        <div style="font-size:10px;color:${C.muted};margin-top:3px;">${izq.sub}</div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:9.5px;color:${C.muted};font-weight:700;letter-spacing:.3px;text-transform:uppercase;margin-bottom:4px;">${der.label}</div>
        <div style="font-size:14.5px;font-weight:800;color:${der.color};">${der.valor}</div>
        <div style="font-size:10px;color:${C.muted};margin-top:3px;">${der.sub}</div>
      </div>
    </div>`;

  const ocpPendPago = state.data.ocp.filter(o => o.estado_pago !== "pagada" && o.estado !== "cancelada").length;
  const ocpPagadas  = state.data.ocp.filter(o => o.estado_pago === "pagada").length;

  return `
    <div id="dash-root">
      <style>
        #dash-root .dcard{transition:transform .14s ease, box-shadow .14s ease;}
        #dash-root .dcard.clk{cursor:pointer;}
        #dash-root .dcard.clk:hover{transform:translateY(-1px);box-shadow:0 4px 14px rgba(16,35,74,.09);}
        #dash-root .chev{display:flex;align-items:center;justify-content:center;color:#cbd5e1;font-size:22px;font-weight:400;flex:0 0 auto;}
        @media(max-width:860px){#dash-root .flow{flex-direction:column;} #dash-root .chev{transform:rotate(90deg);height:12px;}}
      </style>

      ${hero}

      ${bannerPuestaEnMarcha()}

      ${backupBannerHTML}

      ${alertasHTML}

      ${SEC("Flujo de trabajo")}
      ${flowDiagram}

      ${SEC("Resumen por documento")}
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(165px,1fr));gap:9px;">
        ${kpis}
      </div>

      ${SEC("Rentabilidad")}
      ${margenSection}

      ${SEC("Desglose por estado")}
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(270px,1fr));gap:9px;">
        ${crearDesgloseCard("CT", ctEstados)}
        ${crearDesgloseCard("OP", opEstados)}
        ${crearDesgloseCard("OCP", ocpEstados)}
        ${crearDesgloseCard("RC", rcEstados)}
      </div>

      ${SEC("Indicadores financieros")}
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:9px;margin-bottom:9px;">
        ${indicador(C.blue, "💰", "Total cotizado", fmtCOP(totalCT), `${state.data.cotizaciones.length} cotizaciones`)}
        ${indicador(C.amber, "📦", "Total en producción", fmtCOP(totalOP), `${state.data.pedidos.length} órdenes`)}
        ${indicador(C.purple, "👥", "Base de clientes", `${state.data.clientes.length}`, "clientes registrados")}
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(310px,1fr));gap:9px;">
        ${dobleCard("cxp",
          { label:"Cuentas por pagar", valor:fmtCOP(porPagar), color:"#d97706", sub:`${ocpPendPago} órdenes pendientes de pago` },
          { label:"Pagado", valor:fmtCOP(getTotalPagado()), color:"#10b981", sub:`${ocpPagadas} órdenes pagadas` },
          "#e0a53a")}
        ${dobleCard("FACT",
          { label:"Cuentas por cobrar", valor:fmtCOP(porCobrar), color:"#059669", sub:`${factPendCobro} factura${factPendCobro!==1?"s":""} por cobrar` },
          { label:"Cobrado", valor:fmtCOP(totalCobrado), color:"#047857", sub:`${factPagadas} factura${factPagadas!==1?"s":""} pagadas` },
          "#6ee7b7")}
      </div>
    </div>
  `;
}

import { registerActions } from "../ui/actions.js";

registerActions({
  "dash-margen-periodo": (d, el, e) => {{
      state.dashMargenPeriodo = d.periodo || "mes";
      render();
      return;
    }},
});
